/**
 * Enhanced PWA Install Prompt Component
 * Provides a custom UI for app installation with proper UX.
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Download, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [firstVisit, setFirstVisit] = useState(false);

  // Always call the hook at the top level to respect Hook rules
  const reduce = useReducedMotion();

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Key used to track whether we've already shown the install UI on this device
    const shownKey = 'pwa-install-shown';
    const dismissedDate = localStorage.getItem('pwa-install-dismissed');
    const alreadyShown = !!localStorage.getItem(shownKey);

    // First visit (no shown key) => mark and make eligible to force prompt
    if (!alreadyShown) {
      setFirstVisit(true);
      // pre-show our UI so the user sees messaging immediately on first visit
      setShowPrompt(true);
    }

    if (dismissedDate) {
      const daysSinceDismissed = (Date.now() - Number.parseInt(dismissedDate)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        setDismissed(true);
        return;
      }
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);

      // If this is the user's first visit on this device, immediately trigger the
      // browser install prompt to ensure the install flow is offered right away.
      try {
        if (!localStorage.getItem(shownKey)) {
          // mark as shown to avoid repeated automatic prompts
          localStorage.setItem(shownKey, Date.now().toString());
          // call the browser prompt immediately and handle the result
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (event as any).prompt();
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (event as any).userChoice.then(({ outcome }: any) => {
            if (outcome === 'accepted') {
              setIsInstalled(true);
              setShowPrompt(false);
            }
            setDeferredPrompt(null);
          });
          setShowPrompt(true);
          return;
        }
      } catch (e) {
        // ignore prompt errors, fall back to showing the app UI
      }

      setShowPrompt(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowPrompt(false);
      localStorage.removeItem('pwa-install-dismissed');
      try {
        localStorage.setItem(shownKey, Date.now().toString());
      } catch {}
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Test helper (enabled only in DEV or with localStorage flag) to show the prompt
    // deterministically for E2E tests. Guarded so production is unaffected.
    try {
      const enableTestHelpers = localStorage.getItem('cges:enable_test_helpers') === '1';
      if (import.meta.env.DEV || enableTestHelpers) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).__cges_show_install_prompt = (deferredPrompt?: any) => {
          const payload = deferredPrompt ?? ({
            prompt: () => Promise.resolve(),
            userChoice: Promise.resolve({ outcome: 'dismissed' }),
          } as any);
          setDeferredPrompt(payload);
          setShowPrompt(true);
          try {
            // expose current state for tests to inspect: whether a deferredPrompt exists
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).__cges_install_state = { hasDeferred: !!payload, showPrompt: true };
          } catch {}
        };
        try {
          // Expose a small mount marker for tests to validate that the hook ran
          // Guarded so production doesn't see test globals
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any).__cges_install_prompt_mounted = true;
        } catch {}
      }
    } catch (e) {
      // ignore access errors
    }

    return () => {
      try {
        // cleanup test helper if present
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((window as any).__cges_show_install_prompt) delete (window as any).__cges_show_install_prompt;
      } catch {}

      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    const shownKey = 'pwa-install-shown';
    try {
      if (deferredPrompt) {
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        try { localStorage.setItem(shownKey, Date.now().toString()); } catch {}

        if (outcome === 'accepted') {
          setShowPrompt(false);
          setIsInstalled(true);
        } else {
          setShowPrompt(false);
          setDismissed(true);
          localStorage.setItem('pwa-install-dismissed', Date.now().toString());
        }

        // best-effort analytics event
        import('@/lib/analytics').then((m) => { try { (m.default as any).trackEvent('PWA_INSTALL', { outcome }); } catch {} });
        setDeferredPrompt(null);
      } else {
        // no browser prompt available (iOS / unsupported). Keep UI open to show manual instructions
        setShowPrompt(true);
      }
    } catch (e) {
      // ignore prompt errors
    }
  };

  const handleDismiss = () => {
    try { localStorage.setItem('pwa-install-shown', Date.now().toString()); } catch {}
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Show the prompt UI when eligible — even if `deferredPrompt` is not
  // yet available (this covers platforms without `beforeinstallprompt`, and
  // allows us to present fallback instructions on first-visit devices).
  if (isInstalled || dismissed || !showPrompt) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={reduce ? undefined : { y: 100, opacity: 0 }}
        animate={reduce ? undefined : { y: 0, opacity: 1 }}
        exit={reduce ? undefined : { y: 100, opacity: 0 }}
        className="fixed bottom-4 left-4 right-4 z-50 sm:left-auto sm:right-4 sm:w-96"
      >
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <Download className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-sm mb-1">Install bOrGamer</h3>
                <p className="text-xs text-muted-foreground mb-3">Get quick access and offline support</p>
                <div className="flex gap-2">
                  {deferredPrompt ? (
                    <Button size="sm" onClick={handleInstall} className="flex-1">
                      Install
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => {
                        try {
                          window.location.hash = '#/help/pwa-install';
                        } catch (e) {}
                      }}
                      className="flex-1"
                    >
                      How to install
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={handleDismiss}>
                    Not now
                  </Button>
                </div>
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6"
                onClick={handleDismiss}
                aria-label="Dismiss install prompt"
                title="Dismiss"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
}
